import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import type { FieldSuggestion } from '@frontend/api';
import {
  AssistanceAvailableIcon,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
  Cascader,
  CascaderComponents,
  type CascaderCommonProps,
  type CascaderOption,
  type CascaderComponentProps,
  type CascaderComponentTypes,
  CascaderPositioningOptions,
} from '@frontend/legacy-components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { initMaterialsStore } from 'store/materials/actions';
import {
  getStoredMaterialOptionsForCascader,
  getMaterialClassByMaterialId,
  getMaterialFamilyByMaterialId,
} from 'store/materials/reducer';
import { useFeatureFlagValue } from 'store/session/hooks';

import { adaptMaterialOptionsToCascaderSubmenu } from './adaptMaterialOptionsToCascader';
import {
  buildDecoratedMaterialSubmenu,
  dedupeMaterialSuggestionEntries,
  filterMaterialFieldSuggestions,
  getMaterialSuggestionBannerTitle,
  getMaterialSuggestionCommitPath,
  getMaterialSuggestionJumpPath,
  listParsableMaterialSuggestions,
  type MaterialSuggestionEntry,
} from './applyMaterialSuggestionDecoration';
import type { MaterialCascaderSuggestionOption } from './materialCascaderSuggestionTypes';
import { materialSuggestionCascaderComponents } from './materialSuggestionCascaderComponents';
import type {
  MaterialSelectorChangeHandler,
  MaterialCascaderNode,
} from './types';

export type MaterialCascaderValuePath = (string | number)[];

/**
 * Matches {@link SignalSelect} `MenuWrapper`: column shell with a horizontal row for branch
 * columns so headers/footers/banners stack above or below the navigable columns.
 */
const materialSuggestionMenuContainerCss = {
  display: 'flex',
  flexDirection: 'column',
  bg: 'page.surface',
  border: '1px solid',
  borderColor: 'separator.border',
  borderRadius: 'md',
  overflow: 'hidden',
  shadow: 'popover',
  zIndex: 'popover',
  minWidth: 'var(--reference-width)',
  width: 'max-content',
  maxWidth: '800px',
};

const materialSuggestionBranchesRowCss = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'scroll',
  scrollbarWidth: 'none',
};

/** Search dropdown is already column-oriented; keep the same shell as default search wrapper. */
const materialSuggestionSearchContainerCss = {
  display: 'flex',
  flexDirection: 'column',
  bg: 'page.surface',
  border: '1px solid',
  borderColor: 'separator.border',
  borderRadius: 'md',
  overflow: 'hidden',
  shadow: 'popover',
  zIndex: 'popover',
  minWidth: 15,
};

const MATERIAL_SUGGESTION_SINGLE_SELECTED_VALUE =
  materialSuggestionCascaderComponents.SingleSelectedValue!;

const LastLabelSingleSelectedValue = ({
  selectedOption,
  getCommonProps: _,
  ...rest
}: CascaderComponentProps<CascaderOption>['SingleSelectedValue']) => {
  const last =
    selectedOption.fullLabels[selectedOption.fullLabels.length - 1] ?? '';
  return (
    <Box {...rest}>
      <Text size="md" as="span">
        {last}
      </Text>
    </Box>
  );
};

const MaterialCascaderInput = (
  props: CascaderComponentProps<CascaderOption>['Input'],
) => <CascaderComponents.Input {...props} aria-label="Select Material" />;

const SuggestedMaterialInput = materialSuggestionCascaderComponents.Input!;

const MaterialCascaderInputWithSuggestion = (
  props: CascaderComponentProps<MaterialCascaderSuggestionOption>['Input'],
) => <SuggestedMaterialInput {...props} />;

function MaterialCascaderControlWithSuggestion({
  useSuggestionChrome,
  children,
  getCommonProps,
  ...rest
}: CascaderComponentProps<CascaderOption>['Control'] & {
  useSuggestionChrome: boolean;
}) {
  const busy = getCommonProps().getIsLoading();
  const InnerControl = useSuggestionChrome
    ? materialSuggestionCascaderComponents.Control!
    : CascaderComponents.Control;
  return (
    <Box position="relative" width="100%">
      <InnerControl getCommonProps={getCommonProps} {...rest}>
        {children}
      </InnerControl>
      {busy ? (
        <Box
          alignItems="center"
          aria-busy
          borderRadius="md"
          display="flex"
          inset={0}
          justifyContent="center"
          pointerEvents="auto"
          position="absolute"
        >
          <Spinner color="feature.secondary" size="sm" />
        </Box>
      ) : null}
    </Box>
  );
}

function MaterialCascaderControlNoSuggestion(
  props: CascaderComponentProps<CascaderOption>['Control'],
) {
  return (
    <MaterialCascaderControlWithSuggestion
      {...props}
      useSuggestionChrome={false}
    />
  );
}

function MaterialCascaderControlSuggestionChrome(
  props: CascaderComponentProps<MaterialCascaderSuggestionOption>['Control'],
) {
  return (
    <MaterialCascaderControlWithSuggestion {...props} useSuggestionChrome />
  );
}

type MaterialSelectorProps = {
  cascaderOptions?: MaterialCascaderNode[];
  canSelectSubmenu?: boolean;
  disabled: boolean;
  onChange: MaterialSelectorChangeHandler;
  placeholder: string;
  materialId: number | null | string;
  materialFieldSuggestions?: FieldSuggestion[];
  shouldSingleSelect?: boolean;
  supplierMaterialId: number | null | string;
  valueArray?: MaterialCascaderValuePath | null;
  searchModePositioning?: CascaderPositioningOptions;
  menuModePositioning?: CascaderPositioningOptions;
};

const toPathStrings = (
  path: MaterialCascaderValuePath | null | undefined,
): string[] => (path ?? []).map(String);

const segmentForParent = (path: string[], index: number): number | string => {
  const s = path[index];
  if (s === undefined || s === '') {
    return '';
  }
  const n = Number(s);
  return Number.isNaN(n) ? s : n;
};

const emptySegmentToNull = (v: number | string): number | string | null =>
  v === '' ? null : v;

const parseMaterialPath = (path: string[]) => ({
  classId: segmentForParent(path, 0),
  familyId: segmentForParent(path, 1),
  materialId: segmentForParent(path, 2),
  supplierMaterialId: emptySegmentToNull(segmentForParent(path, 3)),
});

const materialHasSupplierTierInOptions = (
  classes: MaterialCascaderNode[],
  mid: number | string | null | undefined,
): boolean => {
  if (mid == null || mid === '') {
    return false;
  }
  const id = String(mid);
  for (const matClass of classes) {
    for (const family of matClass.children ?? []) {
      for (const material of family.children ?? []) {
        if (String(material.value) === id) {
          return (
            Array.isArray(material.children) && material.children.length > 0
          );
        }
      }
    }
  }
  return false;
};

const getMaterialSingleValuePath = (
  materialOptions: MaterialCascaderNode[],
  classId: number | string,
  familyId: number | string,
  mid: number | string | null | undefined,
  supplierMaterialId: number | string | null | undefined,
): MaterialCascaderValuePath | null => {
  if (mid == null || mid === '') {
    return null;
  }
  const useSupplier =
    Boolean(supplierMaterialId && mid) &&
    materialHasSupplierTierInOptions(materialOptions, mid);
  if (useSupplier) {
    return [classId, familyId, mid, supplierMaterialId as number | string];
  }
  return [classId, familyId, mid];
};

const pathToKey = (path: MaterialCascaderValuePath | null) =>
  path?.length ? path.map(String).join('/') : '';

/** True when cascader value paths match (same segment strings). */
const pathsEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => String(v) === String(b[i]));

const MaterialSuggestionBannerRow = ({
  entry,
  materialOptions,
  getCommonProps,
  isLast,
}: {
  entry: MaterialSuggestionEntry;
  materialOptions: MaterialCascaderNode[];
  getCommonProps: () => CascaderCommonProps<MaterialCascaderSuggestionOption>;
  isLast: boolean;
}) => {
  const title = getMaterialSuggestionBannerTitle(entry);
  const { target } = entry;
  const sourceName = entry.fieldSuggestion.sourceName;
  const jumpPath = getMaterialSuggestionJumpPath(materialOptions, target);
  const commitPath = getMaterialSuggestionCommitPath(materialOptions, target);

  const onJump = () => {
    if (!jumpPath?.length) {
      return;
    }
    getCommonProps().jumpToMenuValuePath(jumpPath);
  };

  const onSelect = () => {
    if (!commitPath?.length) {
      return;
    }
    getCommonProps().commitValuePath(commitPath);
  };

  const isFamily = target.kind === 'family';

  return (
    <Flex
      align="flex-start"
      gap={2}
      px={1.5}
      py={1}
      borderBottomWidth={isLast ? 0 : '1px'}
      borderColor="separator.divider"
    >
      <Box flexShrink={0} pt={0.5}>
        <AssistanceAvailableIcon boxSize={4} color="interaction.suggestion" />
      </Box>
      <VStack align="flex-start" flex={1} gap={0}>
        <Text fontWeight={600} size="md" color="feature.primary" noOfLines={1}>
          {title}
        </Text>
        {sourceName ? (
          <Text size="sm" color="interaction.suggestion" noOfLines={2}>
            From {sourceName}
          </Text>
        ) : null}
      </VStack>
      <Box
        alignSelf="stretch"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {isFamily ? (
          <Button
            flexShrink={0}
            size="sm"
            type="button"
            variant="secondary"
            colorScheme="suggestion"
            onClick={onJump}
            isDisabled={!jumpPath?.length}
          >
            JUMP TO
          </Button>
        ) : (
          <Button
            flexShrink={0}
            size="sm"
            type="button"
            variant="primary"
            colorScheme="suggestion"
            onClick={onSelect}
            isDisabled={!commitPath?.length}
          >
            SELECT
          </Button>
        )}
      </Box>
    </Flex>
  );
};

const MaterialSelectorBannerShellContext = createContext<{
  bannerEntries: MaterialSuggestionEntry[];
  materialOptions: MaterialCascaderNode[];
} | null>(null);

function MaterialSelectorMenuWrapperWithSuggestionBanners(
  props: CascaderComponentProps<MaterialCascaderSuggestionOption>['MenuWrapper'],
) {
  const ctx = useContext(MaterialSelectorBannerShellContext);
  if (!ctx) {
    throw new Error(
      'MaterialSelectorMenuWrapperWithSuggestionBanners must be used within MaterialSelectorBannerShellContext',
    );
  }
  const { bannerEntries, materialOptions } = ctx;
  const { getCommonProps, children, menuRef, scrollRef, ...rest } = props;

  return (
    <Box
      ref={menuRef}
      {...rest}
      __css={materialSuggestionMenuContainerCss}
      className="cascader-menu-wrapper"
    >
      <VStack
        align="stretch"
        spacing={0}
        flexShrink={0}
        alignSelf="stretch"
        bg="interaction.surfaceSuggestionHover"
        borderBottomWidth="1px"
        borderColor="separator.divider"
      >
        {bannerEntries.map((entry, idx) => (
          <MaterialSuggestionBannerRow
            key={entry.fieldSuggestion.id ?? idx}
            entry={entry}
            materialOptions={materialOptions}
            getCommonProps={getCommonProps}
            isLast={idx === bannerEntries.length - 1}
          />
        ))}
      </VStack>
      <Box ref={scrollRef} __css={materialSuggestionBranchesRowCss}>
        {children}
      </Box>
    </Box>
  );
}

function MaterialSelectorSearchWrapperWithSuggestionShell(
  props: CascaderComponentProps<MaterialCascaderSuggestionOption>['SearchWrapper'],
) {
  const { getCommonProps, children, ...rest } = props;
  return (
    <CascaderComponents.SearchWrapper
      getCommonProps={getCommonProps}
      {...rest}
      __css={materialSuggestionSearchContainerCss}
    >
      <Box flex={1} minHeight={0}>
        {children}
      </Box>
    </CascaderComponents.SearchWrapper>
  );
}

const MaterialSelector = ({
  cascaderOptions,
  canSelectSubmenu = false,
  disabled,
  onChange,
  placeholder,
  materialId,
  materialFieldSuggestions,
  shouldSingleSelect = true,
  supplierMaterialId,
  valueArray,
  // NB: This is bad prop design, they should be spread, but right now, the props are too coupled one level up and it's risky to pass them any other way. Sorry.
  searchModePositioning,
  menuModePositioning,
}: MaterialSelectorProps) => {
  const dispatch = useDispatch();
  const initMaterialStore = useCallback(() => {
    dispatch(initMaterialsStore());
  }, [dispatch]);

  const materialOptions = useAppSelector(
    (state) => cascaderOptions || getStoredMaterialOptionsForCascader(state),
  );
  const materialClass = useAppSelector((state) =>
    getMaterialClassByMaterialId(state, materialId),
  );
  const materialFamily = useAppSelector((state) =>
    getMaterialFamilyByMaterialId(state, materialId),
  );

  const [hasInitiated, setHasInitiated] = useState(false);
  const [pendingPathKey, setPendingPathKey] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (materialOptions.length == 0 && !hasInitiated) {
      initMaterialStore();
      setHasInitiated(true);
    }
  }, [hasInitiated, initMaterialStore, materialOptions.length]);

  const isMaterialSuggestionsEnabled = useFeatureFlagValue(
    featureFlagKeys.isMaterialSuggestionsEnabled,
  );
  const resolvedMaterialFieldSuggestions = useMemo(
    () => (isMaterialSuggestionsEnabled ? materialFieldSuggestions : undefined),
    [isMaterialSuggestionsEnabled, materialFieldSuggestions],
  );

  const materialSuggestionFieldList = useMemo(
    () => filterMaterialFieldSuggestions(resolvedMaterialFieldSuggestions),
    [resolvedMaterialFieldSuggestions],
  );

  const materialSuggestionUiEnabled =
    shouldSingleSelect && materialSuggestionFieldList.length > 0;

  const materialClassId = (materialClass || {}).id ?? '';
  const materialFamilyId = (materialFamily || {}).id ?? '';
  const valuePath = useMemo(
    () =>
      shouldSingleSelect
        ? getMaterialSingleValuePath(
            materialOptions,
            materialClassId,
            materialFamilyId,
            materialId,
            supplierMaterialId,
          )
        : valueArray ?? null,
    [
      materialOptions,
      materialClassId,
      materialFamilyId,
      materialId,
      supplierMaterialId,
      shouldSingleSelect,
      valueArray,
    ],
  );

  const valueStrings = useMemo(
    () => (valuePath ? toPathStrings(valuePath) : []),
    [valuePath],
  );
  const cascaderValue = valueStrings;
  const resolvedPathKey = pathToKey(valuePath);

  const materialSuggestionBannerEntries = useMemo(() => {
    const raw = listParsableMaterialSuggestions(
      resolvedMaterialFieldSuggestions ?? [],
    );
    const filtered = raw.filter((entry) => {
      if (entry.target.kind === 'family') {
        const jumpPath = getMaterialSuggestionJumpPath(
          materialOptions,
          entry.target,
        );
        if (jumpPath == null) {
          return false;
        }
        if (pathsEqual(valueStrings, jumpPath)) {
          return false;
        }
        return true;
      }
      const commitPath = getMaterialSuggestionCommitPath(
        materialOptions,
        entry.target,
      );
      if (commitPath == null) {
        return false;
      }
      if (pathsEqual(valueStrings, commitPath)) {
        return false;
      }
      return true;
    });
    const deduped = dedupeMaterialSuggestionEntries(filtered);
    // More than three parsable material suggestions usually indicates a noisy print extraction;
    // cap banners so the cascader stays usable.
    return deduped.slice(0, 3);
  }, [resolvedMaterialFieldSuggestions, materialOptions, valueStrings]);

  const cascaderSubmenu = useMemo(() => {
    const base = adaptMaterialOptionsToCascaderSubmenu(materialOptions);
    if (!materialSuggestionUiEnabled) {
      return base;
    }
    return buildDecoratedMaterialSubmenu({
      cascaderSubmenu: base,
      materialClasses: materialOptions,
      fieldSuggestions: materialSuggestionFieldList,
    });
  }, [
    materialOptions,
    materialSuggestionFieldList,
    materialSuggestionUiEnabled,
  ]);

  const showSuggestionBanners =
    materialSuggestionUiEnabled && materialSuggestionBannerEntries.length > 0;

  const bannerShellContextValue = useMemo(
    () => ({
      bannerEntries: materialSuggestionBannerEntries,
      materialOptions,
    }),
    [materialSuggestionBannerEntries, materialOptions],
  );

  const components = useMemo(() => {
    if (!materialSuggestionUiEnabled) {
      return {
        Control: MaterialCascaderControlNoSuggestion,
        Input: MaterialCascaderInput,
        ...(shouldSingleSelect
          ? { SingleSelectedValue: LastLabelSingleSelectedValue }
          : {}),
      };
    }

    const suggestionBase: Partial<
      CascaderComponentTypes<MaterialCascaderSuggestionOption>
    > = {
      ...materialSuggestionCascaderComponents,
      Control: MaterialCascaderControlSuggestionChrome,
      Input: MaterialCascaderInputWithSuggestion,
      ...(shouldSingleSelect
        ? { SingleSelectedValue: MATERIAL_SUGGESTION_SINGLE_SELECTED_VALUE }
        : {}),
    };

    if (!showSuggestionBanners) {
      return suggestionBase;
    }

    return {
      ...suggestionBase,
      MenuWrapper: MaterialSelectorMenuWrapperWithSuggestionBanners,
      SearchWrapper: MaterialSelectorSearchWrapperWithSuggestionShell,
    };
  }, [materialSuggestionUiEnabled, shouldSingleSelect, showSuggestionBanners]);

  useEffect(() => {
    if (pendingPathKey === undefined) {
      return;
    }
    if (pendingPathKey === resolvedPathKey) {
      setPendingPathKey(undefined);
    }
  }, [pendingPathKey, resolvedPathKey]);

  const isLoadingSelection =
    shouldSingleSelect &&
    pendingPathKey !== undefined &&
    pendingPathKey !== resolvedPathKey;

  const handleLibraryChange = useCallback(
    (selectedPath: string[]) => {
      if (!selectedPath.length) {
        if (shouldSingleSelect) {
          setPendingPathKey('');
        }
        onChange({
          classId: null,
          familyId: null,
          materialId: null,
          supplierMaterialId: null,
        });
        return;
      }

      const { classId, familyId, materialId, supplierMaterialId } =
        parseMaterialPath(selectedPath);

      if (shouldSingleSelect) {
        const hasSupplierTier = materialHasSupplierTierInOptions(
          materialOptions,
          materialId,
        );
        const isMaterialFullySpecified =
          materialId !== '' && (!hasSupplierTier || supplierMaterialId != null);

        if (isMaterialFullySpecified) {
          const nextPath = getMaterialSingleValuePath(
            materialOptions,
            classId,
            familyId,
            materialId,
            supplierMaterialId,
          );
          setPendingPathKey(pathToKey(nextPath));
        }
      }

      onChange({ classId, familyId, materialId, supplierMaterialId });
    },
    [materialOptions, shouldSingleSelect, onChange],
  );

  const sharedCascaderProps = {
    canSelectSubmenu,
    className: 'material-selector-cascader',
    components,
    isClearable: true,
    isDisabled: disabled,
    isLoading: !disabled && isLoadingSelection,
    onChange: handleLibraryChange,
    options: cascaderSubmenu,
    placeholder,
    value: cascaderValue,
    searchModePositioning,
    menuModePositioning,
  };

  /** Always wrap with Provider so toggling `showSuggestionBanners` does not remount Cascader (only `components` changes). MenuWrapper reads context only when banners are active. */
  const materialSuggestionCascader = (
    <MaterialSelectorBannerShellContext.Provider
      value={bannerShellContextValue}
    >
      <Cascader<MaterialCascaderSuggestionOption> {...sharedCascaderProps} />
    </MaterialSelectorBannerShellContext.Provider>
  );

  return (
    <Box
      position="relative"
      width="100%"
      bg={materialSuggestionUiEnabled ? 'page.surface' : undefined}
    >
      {materialSuggestionUiEnabled ? (
        materialSuggestionCascader
      ) : (
        <Cascader<CascaderOption> {...sharedCascaderProps} />
      )}
    </Box>
  );
};

export default MaterialSelector;
